import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle2, ChevronLeft, ShieldCheck, XCircle } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchFeatureFlags, fetchFlagSchema, setFlagSchema, type AdminFeatureFlag, type FlagSchemaInfo } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { PermissionDenied } from '@/components/States'

const TYPES = ['', 'bool', 'int', 'percent', 'enum', 'string'] as const
type SchemaType = (typeof TYPES)[number]

/**
 * A14.14 — Flag qiymat-sxemasi (validatsiya). Har flagga tur (bool/int/percent/
 * enum/string) + cheklov belgilanadi. Sxema o'rnatilgach backend `SetFlag` mos
 * KELMAGAN qiymatni RAD etadi (A14.03/04 yozuvlari ham). Joriy qiymat mosligi
 * jonli ko'rsatiladi. Yozuv `POST /admin/settings/flags/schema`. AUDIT_VIEW-gated.
 */
export function FlagSchema() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const [params, setParams] = useSearchParams()
  const selected = params.get('key') ?? ''
  const [flags, setFlags] = useState<AdminFeatureFlag[] | null>(null)
  const [info, setInfo] = useState<FlagSchemaInfo | null>(null)
  const [type, setType] = useState<SchemaType>('')
  const [min, setMin] = useState('')
  const [max, setMax] = useState('')
  const [values, setValues] = useState('')
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    fetchFeatureFlags({ filters: [], sort: [], page: 1, pageSize: 500, search: '', signal: new AbortController().signal })
      .then((r) => setFlags(r.rows))
      .catch(() => setFlags([]))
  }, [])

  const load = useCallback((key: string) => {
    if (!key) {
      setInfo(null)
      return
    }
    setLoading(true)
    fetchFlagSchema(key)
      .then((d) => {
        setInfo(d)
        setType((d.schema.type ?? '') as SchemaType)
        setMin(d.schema.min != null ? String(d.schema.min) : '')
        setMax(d.schema.max != null ? String(d.schema.max) : '')
        setValues((d.schema.values ?? []).join(', '))
      })
      .catch(() => setInfo(null))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load(selected)
  }, [selected, load])

  const selectKey = (key: string) => setParams(key ? { key } : {}, { replace: true })

  const save = () => {
    if (!selected || busy) return
    const def: { type: string; min?: number | null; max?: number | null; values?: string[] } = { type }
    if (type === 'int') {
      def.min = min.trim() === '' ? null : Number(min)
      def.max = max.trim() === '' ? null : Number(max)
    }
    if (type === 'enum') {
      def.values = values.split(',').map((s) => s.trim()).filter(Boolean)
    }
    setBusy(true)
    setFlagSchema(selected, def)
      .then(() => {
        toast(type === '' ? t.flags.schemaCleared : t.flags.schemaSaved, { tone: 'success' })
        load(selected)
      })
      .catch((e: unknown) => toast(t.flags.toggleFailed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) }))
      .finally(() => setBusy(false))
  }

  if (!can(PERMISSIONS.AUDIT_VIEW)) return <PermissionDenied permission={PERMISSIONS.AUDIT_VIEW} />

  return (
    <>
      <PageHeader
        title={t.flags.schemaTitle}
        screenId="A14.14"
        actions={
          <Link
            to="/flags"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.flags.title}
          </Link>
        }
      />
      <div className="border-b border-border-subtle p-3">
        <label className="mb-1 block text-xs text-tertiary">{t.flags.key}</label>
        <select
          value={selected}
          onChange={(e) => selectKey(e.target.value)}
          className="w-full max-w-md rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 font-mono text-sm text-primary outline-none"
        >
          <option value="">— {t.flags.pickFlag} —</option>
          {(flags ?? []).map((f) => (
            <option key={f.key} value={f.key}>{f.key}</option>
          ))}
        </select>
      </div>

      {!selected ? (
        <div className="flex-1 px-3 py-10 text-center text-sm text-tertiary">
          <ShieldCheck size={24} className="mx-auto mb-2 opacity-60" />
          {t.flags.pickFlag}
        </div>
      ) : loading ? (
        <div className="flex-1 p-6 text-center text-sm text-tertiary">…</div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto p-3">
          <div className="max-w-xl space-y-5">
            {/* Current value conformance */}
            {info && (
              <div className="flex items-center gap-2 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm">
                <span className="text-tertiary">{t.flags.value}:</span>
                <span className="font-mono text-primary">{info.value || '—'}</span>
                {info.schema.type ? (
                  info.valid ? (
                    <span className="ml-auto inline-flex items-center gap-1 text-success">
                      <CheckCircle2 size={14} /> {t.flags.schemaValid}
                    </span>
                  ) : (
                    <span className="ml-auto inline-flex items-center gap-1 text-danger" title={info.reason}>
                      <XCircle size={14} /> {t.flags.schemaInvalid}
                    </span>
                  )
                ) : (
                  <span className="ml-auto text-tertiary">{t.flags.schemaNone}</span>
                )}
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs text-tertiary">{t.flags.schemaType}</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as SchemaType)}
                className="w-full rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 text-sm text-primary outline-none"
              >
                <option value="">{t.flags.schemaNone}</option>
                <option value="bool">bool</option>
                <option value="int">int</option>
                <option value="percent">percent</option>
                <option value="enum">enum</option>
                <option value="string">string</option>
              </select>
            </div>

            {type === 'int' && (
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="mb-1 block text-xs text-tertiary">min</label>
                  <input
                    type="number"
                    value={min}
                    onChange={(e) => setMin(e.target.value)}
                    className="w-full rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 text-sm text-primary outline-none"
                  />
                </div>
                <div className="flex-1">
                  <label className="mb-1 block text-xs text-tertiary">max</label>
                  <input
                    type="number"
                    value={max}
                    onChange={(e) => setMax(e.target.value)}
                    className="w-full rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 text-sm text-primary outline-none"
                  />
                </div>
              </div>
            )}

            {type === 'enum' && (
              <div>
                <label className="mb-1 block text-xs text-tertiary">{t.flags.schemaValues}</label>
                <input
                  value={values}
                  onChange={(e) => setValues(e.target.value)}
                  placeholder="light, dark, auto"
                  className="w-full rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 text-sm text-primary outline-none"
                />
                <p className="mt-1 text-xs text-tertiary">{t.flags.schemaValuesHint}</p>
              </div>
            )}

            <div className="flex items-center gap-2 border-t border-border-subtle pt-4">
              <button
                onClick={save}
                disabled={busy}
                className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
              >
                {t.common.save}
              </button>
              {type !== '' && (
                <button
                  onClick={() => setType('')}
                  className="rounded-md border border-border-default bg-surface-2 px-4 py-1.5 text-sm text-secondary hover:text-primary"
                >
                  {t.flags.schemaClear}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
